// Gambiko : tiroir panier et page /cart (sections gk-panier-tiroir et gk-panier-page, snippet cart-content).
// Mises à jour : API Cart AJAX (/cart/add.js, /cart/change.js, /cart/update.js) + Section Rendering (paramètre sections).
// Ouverture du tiroir : événement cart:open ou méthode open() (appelée par <cart-icon>).
// Accessibilité : focus piégé dans le tiroir, Échap / voile / croix pour fermer, focus rendu au bouton d'origine,
// annonces dans une zone aria-live.
import { ThemeEvents } from '@theme/events';

const ROUTES = () => window.Theme?.routes || { cart_add_url: '/cart/add', cart_change_url: '/cart/change', cart_update_url: '/cart/update', cart_url: '/cart', root_url: '/' };
const FOCUSABLE = 'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';
const NOUVEAU_MS = 2600;

// Jours fériés français (fixes + lundi de Pâques, Ascension, lundi de Pentecôte) : ils ne comptent pas comme jours ouvrés.
const paques = (a) => {
  const f = Math.floor, g = a % 19, c = f(a / 100), h = (c - f(c / 4) - f((8 * c + 13) / 25) + 19 * g + 15) % 30;
  const i = h - f(h / 28) * (1 - f(29 / (h + 1)) * f((21 - g) / 11)), j = (a + f(a / 4) + i + 2 - c + f(c / 4)) % 7;
  const l = i - j, m = 3 + f((l + 40) / 44), d = l + 28 - 31 * f(m / 4);
  return new Date(a, m - 1, d);
};
const feries = {};
const estFerie = (d) => {
  const a = d.getFullYear();
  if (!feries[a]) {
    const p = paques(a), plus = (n) => { const x = new Date(p); x.setDate(x.getDate() + n); return `${x.getMonth() + 1}-${x.getDate()}`; };
    feries[a] = new Set(['1-1', '5-1', '5-8', '7-14', '8-15', '11-1', '11-11', '12-25', plus(1), plus(39), plus(50)]);
  }
  return feries[a].has(`${d.getMonth() + 1}-${d.getDate()}`);
};
// Jours ouvrés (lundi à vendredi, hors jours fériés), comme la fiche produit.
const ouvres = (from, n, sens = 1) => {
  const d = new Date(from);
  let k = 0;
  while (k < n) { d.setDate(d.getDate() + sens); if (d.getDay() !== 0 && d.getDay() !== 6 && !estFerie(d)) k++; }
  return d;
};
const tpl = (s, vars) => s.replace(/\[(\w+)\]/g, (_, k) => vars[k] ?? '');

class GkPanier extends HTMLElement {
  connectedCallback() {
    this.vue = this.dataset.vue;
    this.sectionId = this.dataset.sectionId;
    this.opener = null;
    this.timerNote = null;

    this.onClick = this.onClick.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onKey = this.onKey.bind(this);
    this.onOpenEvent = () => this.open();
    this.onCloseEvent = () => this.close();
    this.onCartUpdated = (e) => { if (e.detail?.data?.source !== 'gk-panier') this.refresh(); };
    this.onSync = (e) => { if (e.detail !== this) this.refresh(); };

    this.addEventListener('click', this.onClick);
    this.addEventListener('input', this.onInput);
    this.addEventListener('keydown', this.onKey);
    document.addEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    document.addEventListener('gk-panier:maj', this.onSync);
    if (this.vue === 'tiroir') {
      document.addEventListener(ThemeEvents.cartOpen, this.onOpenEvent);
      document.addEventListener(ThemeEvents.cartClose, this.onCloseEvent);
    }
    this.apresRendu();
  }

  disconnectedCallback() {
    document.removeEventListener(ThemeEvents.cartUpdated, this.onCartUpdated);
    document.removeEventListener('gk-panier:maj', this.onSync);
    document.removeEventListener(ThemeEvents.cartOpen, this.onOpenEvent);
    document.removeEventListener(ThemeEvents.cartClose, this.onCloseEvent);
  }

  get cible() { return this.querySelector('[data-gk-pan-cible]'); }
  get panneau() { return this.querySelector('.gk-pan__panneau'); }

  /* ---------- Ouverture / fermeture (tiroir) ---------- */
  open() {
    if (this.vue !== 'tiroir') return;
    if (!this.hidden && this.classList.contains('is-ouvert')) return;
    this.opener = document.activeElement;
    this.hidden = false;
    document.body.classList.add('overflow-hidden');
    requestAnimationFrame(() => {
      this.classList.add('is-ouvert');
      (this.querySelector('[data-gk-pan-fermer].gk-pan__fermer') || this.panneau)?.focus();
    });
  }

  close() {
    if (this.vue !== 'tiroir' || this.hidden) return;
    this.classList.remove('is-ouvert');
    document.body.classList.remove('overflow-hidden');
    const fin = () => { this.hidden = true; };
    const reduit = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    reduit ? fin() : setTimeout(fin, 300);
    if (this.opener && document.contains(this.opener)) this.opener.focus();
    this.opener = null;
  }

  isOpen() { return this.vue === 'tiroir' && !this.hidden; }

  onKey(e) {
    if (this.vue !== 'tiroir' || this.hidden) return;
    if (e.key === 'Escape') { e.preventDefault(); this.close(); return; }
    if (e.key !== 'Tab') return;
    const items = [...this.panneau.querySelectorAll(FOCUSABLE)].filter((el) => el.offsetParent !== null || el === document.activeElement);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  /* ---------- Clics ---------- */
  async onClick(e) {
    const t = e.target;
    if (t.closest('[data-gk-pan-fermer]')) { this.close(); return; }

    const qte = t.closest('[data-gk-pan-qte]');
    if (qte) {
      const li = qte.closest('[data-key]');
      const n = Number(li.dataset.quantity) + Number(qte.dataset.gkPanQte);
      if (n >= 1) this.changer(li, n);
      return;
    }
    const retirer = t.closest('[data-gk-pan-retirer]');
    if (retirer) { this.changer(retirer.closest('[data-key]'), 0); return; }

    const ajout = t.closest('[data-gk-sugg-add]');
    if (ajout) { this.ajouter(ajout); return; }

    const varBtn = t.closest('[data-gk-sugg-var]');
    if (varBtn) {
      const ouvert = varBtn.getAttribute('aria-expanded') === 'true';
      varBtn.setAttribute('aria-expanded', String(!ouvert));
      const liste = this.querySelector(`#${varBtn.getAttribute('aria-controls')}`);
      liste.hidden = ouvert;
      if (!ouvert) liste.querySelector('[aria-checked="true"]')?.focus();
      return;
    }
    const opt = t.closest('[data-gk-sugg-opt]');
    if (opt) { this.choisirVariante(opt); return; }

    const motBtn = t.closest('[data-gk-pan-mot-bouton]');
    if (motBtn) { this.basculerMot(motBtn); }
  }

  onInput(e) {
    const ta = e.target.closest('[data-gk-pan-mot-texte]');
    if (!ta) return;
    const compte = this.querySelector('[data-gk-pan-mot-compte]');
    if (compte) compte.textContent = `${ta.value.length} / 200`;
    clearTimeout(this.timerNote);
    this.timerNote = setTimeout(() => this.enregistrerNote(ta.value), 600);
  }

  /* ---------- Variante d'une suggestion ---------- */
  choisirVariante(opt) {
    const carte = opt.closest('[data-gk-sugg]');
    carte.querySelectorAll('[data-gk-sugg-opt]').forEach((o) => {
      const on = o === opt;
      o.setAttribute('aria-checked', String(on));
      o.tabIndex = on ? 0 : -1;
    });
    carte.querySelector('[data-gk-sugg-add]').dataset.gkSuggAdd = opt.dataset.gkSuggOpt;
    carte.querySelector('[data-gk-sugg-val]').textContent = opt.textContent.trim();
    carte.querySelector('[data-gk-sugg-prix]').textContent = opt.dataset.prix;
    carte.querySelectorAll('a[href]').forEach((a) => { a.href = opt.dataset.url; });
    const img = carte.querySelector('[data-gk-sugg-img]');
    if (img && opt.dataset.img) { img.src = opt.dataset.img; img.removeAttribute('srcset'); }
    const varBtn = carte.querySelector('[data-gk-sugg-var]');
    varBtn.setAttribute('aria-expanded', 'false');
    opt.closest('[role="radiogroup"]').hidden = true;
    varBtn.focus();
  }

  /* ---------- Requêtes panier ---------- */
  async requete(url, corps) {
    const res = await fetch(`${url}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...corps, sections: this.sectionId, sections_url: window.location.pathname }),
    });
    const data = await res.json();
    if (!res.ok || data.status) throw new Error(data.description || data.message || res.status);
    return data;
  }

  async changer(li, quantite) {
    li.classList.add('is-chargement');
    try {
      const data = await this.requete(ROUTES().cart_change_url, { id: li.dataset.key, quantity: quantite });
      this.rendre(data.sections?.[this.sectionId]);
      this.compteur(data.item_count);
      this.annoncer(this.texte('maj'));
      this.synchroniser();
    } catch (err) {
      li.classList.remove('is-chargement');
      this.erreur(err);
    }
  }

  async ajouter(bouton) {
    if (bouton.getAttribute('aria-busy') === 'true') return;
    const id = Number(bouton.dataset.gkSuggAdd);
    const label = bouton.textContent;
    const nom = bouton.closest('[data-gk-sugg], li')?.querySelector('.gk-pan-sugg__nom, .gk-pan-vide__nom')?.textContent.trim() || '';
    bouton.setAttribute('aria-busy', 'true');
    bouton.textContent = bouton.dataset.busy || label;
    this.querySelectorAll('[data-gk-sugg-add]').forEach((b) => { if (b !== bouton) b.disabled = true; });
    try {
      const data = await this.requete(ROUTES().cart_add_url, { items: [{ id, quantity: 1 }] });
      this.rendre(data.sections?.[this.sectionId]);
      const cart = await fetch(`${ROUTES().cart_url}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      this.compteur(cart.item_count);
      this.marquerNouveau(id);
      this.annoncer(tpl(this.texte('ajoute'), { titre: nom }));
      this.synchroniser();
    } catch (err) {
      bouton.removeAttribute('aria-busy');
      bouton.textContent = label;
      this.querySelectorAll('[data-gk-sugg-add]').forEach((b) => { b.disabled = false; });
      this.erreur(err);
    }
  }

  async enregistrerNote(note) {
    try {
      await fetch(`${ROUTES().cart_update_url}.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ note }),
      });
    } catch (err) { this.erreur(err); }
  }

  basculerMot(btn) {
    const champ = this.querySelector(`#${btn.getAttribute('aria-controls')}`);
    const ta = champ.querySelector('textarea');
    const ouvert = btn.getAttribute('aria-expanded') === 'true';
    if (ouvert) {
      ta.value = '';
      this.querySelector('[data-gk-pan-mot-compte]').textContent = '0 / 200';
      clearTimeout(this.timerNote);
      this.enregistrerNote('');
    }
    champ.hidden = ouvert;
    btn.setAttribute('aria-expanded', String(!ouvert));
    btn.querySelector('span').textContent = ouvert ? btn.dataset.ouvrir : btn.dataset.retirer;
    if (!ouvert) ta.focus();
  }

  async refresh() {
    try {
      const url = `${window.location.pathname}?section_id=${this.sectionId}`;
      const html = await fetch(url).then((r) => r.text());
      this.rendre(html);
    } catch (err) { console.error('Gambiko panier :', err); }
  }

  rendre(html) {
    if (!html || !this.cible) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const neuf = doc.querySelector('[data-gk-pan-cible]');
    if (!neuf) return;
    const focusFermer = this.cible.contains(document.activeElement);
    this.cible.innerHTML = neuf.innerHTML;
    this.apresRendu();
    if (focusFermer && this.vue === 'tiroir') this.querySelector('.gk-pan__fermer')?.focus();
  }

  apresRendu() {
    this.dates();
  }

  marquerNouveau(variantId) {
    const li = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (!li) return;
    li.classList.add('is-nouveau');
    const badge = li.querySelector('[data-gk-pan-nouveau]');
    if (badge) badge.hidden = false;
    setTimeout(() => { li.classList.remove('is-nouveau'); if (badge) badge.hidden = true; }, NOUVEAU_MS);
  }

  compteur(n) {
    if (typeof n !== 'number') return;
    document.querySelectorAll('[data-ref="cart-count"]').forEach((el) => { el.textContent = n; });
  }

  synchroniser() { document.dispatchEvent(new CustomEvent('gk-panier:maj', { detail: this })); }

  annoncer(msg) {
    const zone = this.querySelector('[data-gk-pan-annonce]');
    if (!zone || !msg) return;
    zone.textContent = '';
    setTimeout(() => { zone.textContent = msg; }, 50);
  }

  texte(cle) { return this.dataset[`txt${cle[0].toUpperCase()}${cle.slice(1)}`] || ''; }

  erreur(err) {
    console.error('Gambiko panier :', err);
    this.annoncer(this.texte('erreur'));
    document.dispatchEvent(new CustomEvent('toast:open', { detail: { type: 'error', message: this.texte('erreur') } }));
  }

  /* ---------- Dates de livraison ---------- */
  dates() {
    const liv = this.querySelector('[data-gk-pan-dates]');
    const mois = (liv?.dataset.mois || '').split(',');
    // Jour du mois selon la langue : « 1er » en français, « 24. » en allemand.
    const jour = (d) => (document.documentElement.lang || '').startsWith('de') ? `${d.getDate()}.` : (d.getDate() === 1 ? '1er' : String(d.getDate()));
    const jourMois = (d) => `${jour(d)} ${mois[d.getMonth()] || ''}`;
    if (liv && mois.length === 12) {
      const today = new Date();
      const dmin = ouvres(today, Number(liv.dataset.min));
      const dmax = ouvres(today, Number(liv.dataset.max));
      const debut = dmin.getMonth() === dmax.getMonth() ? jour(dmin) : jourMois(dmin);
      liv.textContent = tpl(liv.dataset.modele, { debut, fin: jourMois(dmax) });
      liv.hidden = false;
    }
  }
}

if (!customElements.get('gk-panier')) customElements.define('gk-panier', GkPanier);
